<script setup>
import AuditService from '@/services/quality/audit.service'
import InformationTab from '@/views/quality/master/audit/InformationTab.vue'
import AssessmentTypeTab from '@/views/quality/master/audit/AssessmentTypeTab.vue'
import QuestionnaireTab from '@/views/quality/master/audit/QuestionnaireTab.vue'

definePage({
  meta: {
    name: 'incident-master-audit-setup',
    subject: 'quality.menu',
    action: 'master_audit',
    navActiveLink: 'app-quality-master-audit',
  },
})

const route = useRoute()
const router = useRouter()

const auditId = computed(() => route.params.id ?? null)

const audit = ref(null)
const assessmentTypes = ref([])

// Fetch audit data if editing
if (auditId.value) {
  await useApi(createUrl(`/incident/audits/${auditId.value}`, {
    query: {
      with: ['users'],
    },
  }), {
    afterFetch: ({ data }) => {
      audit.value = data
    },
    onFetchError: () => {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

const onAuditSaved = data => {
  audit.value = data

  // If new audit, update URL to include the ID
  if (!auditId.value && data?.id) {
    router.replace({
      name: 'app-quality-master-audit-setup',
      params: { id: data.id },
    })
  }
}

const onAssessmentTypesUpdated = types => {
  assessmentTypes.value = types
}

// Delete Audit
const isDeleteDialogVisible = ref(false)

const handleDeleteAudit = async isConfirmed => {
  if (isConfirmed && auditId.value) {
    await AuditService.destroy(
      auditId.value,
      () => {
        router.push({ name: 'app-quality-master-audit' })
      },
      () => {},
    )
  }
}
</script>

<template>
  <div>
    <div class="d-flex align-center gap-4 mb-6">
      <IconBtn
        variant="tonal"
        class="rounded"
        size="large"
        @click="router.push({ name: 'app-quality-master-audit' })"
      >
        <VIcon
          size="24"
          icon="tabler-arrow-left"
        />
      </IconBtn>
      <div>
        <div class="text-h5">
          {{ auditId ? 'Edit Audit' : 'Create Audit' }}
        </div>
        <span class="text-body-2 text-medium-emphasis">
          {{ auditId ? audit?.title : 'Set up a new audit with question groups and questions' }}
        </span>
      </div>

      <VSpacer />

      <VBtn
        v-if="auditId"
        variant="flat"
        color="error"
        @click="isDeleteDialogVisible = true"
      >
        <VIcon
          size="20"
          icon="tabler-trash"
          class="me-1"
        />
        Delete Audit
      </VBtn>
    </div>

    <VRow>
      <!-- Left Column: Information + Assessment Types -->
      <VCol
        cols="12"
        md="5"
      >
        <div class="d-flex flex-column gap-6">
          <InformationTab
            :audit="audit"
            @saved="onAuditSaved"
          />

          <template v-if="auditId">
            <AssessmentTypeTab
              :audit-id="auditId"
              @updated="onAssessmentTypesUpdated"
            />
          </template>
          <!--
            <VAlert
            v-else
            color="primary"
            icon="tabler-info-circle"
            variant="tonal"
            >
            Save the audit information first to manage assessment types.
            </VAlert> 
          -->
        </div>
      </VCol>

      <!-- Right Column: Questionnaire -->
      <VCol
        cols="12"
        md="7"
      >
        <template v-if="auditId">
          <QuestionnaireTab
            :audit-id="auditId"
            :assessment-types="assessmentTypes"
          />
        </template>
        <VAlert
          v-else
          color="primary"
          icon="tabler-info-circle"
          variant="tonal"
        >
          Save the audit information first to configure the questionnaire.
        </VAlert>
      </VCol>
    </VRow>
    <!-- Delete Audit Confirmation -->
    <ConfirmDialog
      v-model:is-dialog-visible="isDeleteDialogVisible"
      confirmation-question="Are you sure you want to delete this audit?"
      confirm-title="Delete Audit"
      confirm-msg="This action is permanent. All question groups, questions, and associated data will be permanently removed and cannot be recovered."
      @confirm="handleDeleteAudit"
    />
  </div>
</template>
